import type { IIssueApi, IStoredIssueColConfigs } from "@schemas/issueSchema";
import { IssueFieldEnum } from "@mcapps/shared/enums";
import type {
  FirstDataRenderedEvent,
  GridApi,
  GridReadyEvent,
  IRowNode,
  RowDataUpdatedEvent,
  SelectionChangedEvent,
} from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import "ag-grid-enterprise";
import { AgGridReact, CustomCellRendererProps } from "ag-grid-react";
import React, { useCallback, useEffect, useRef } from "react";
import { useStore } from "react-redux";
import { usePutProjectByProjectIdIssueUpdateSimpleMutation } from "../../../../../../openapi";
import { AG_GRID_LOCALE_NOR } from "../../../../../agGridLocale";
import { useAgGridReduxState } from "../../../../../hooks/useAgGridReduxState";
import { useAppDispatch, useAppSelector } from "../../../../../hooks";
import type { IRootState } from "../../../../../store";
import { getIssueTableGridStateKey } from "../../issueConstants";
import { useDoUpdateIssueMutation } from "../../issueRTKLegacy";
import {
  selectCurrentIssueBoardColumnState,
  selectCurrentIssueColumnsVisibility,
  selectSelectedIssueIdSetOnCurrentIssueBoard,
} from "../../issueSelectors";
import { getIssueColumnStateFromLocalStorage, setSelectedIssueIdsInRedux } from "../../issueSlice";
import IssueTableRoomViewerModal from "./IssueTableRoomViewerModal";
import IssueTableDetailCellRenderer from "./IssueTableDetailCellRenderer";
import {
  defaultIssueTableColDef,
  ISSUE_TABLE_HEADER_HEIGHT,
  ISSUE_TABLE_PAGE_SIZE,
  ISSUE_TABLE_PAGE_SIZE_OPTIONS,
  ISSUE_TABLE_ROW_BUFFER,
  ISSUE_TABLE_ROW_HEIGHT,
  issueTableColumnTypes,
} from "./issueTableGridConfig";
import type { IIssueTableAgGridColumnProps } from "./issueTableTypes";
import { useIssueTableCellEditing } from "./useIssueTableCellEditing";
import { useIssueTableColumns } from "./useIssueTableColumns";
import { useIssueTableColumnStateHandlers } from "./useIssueTableColumnStateHandlers";
import { useIssueTableRoomViewerModal } from "./useIssueTableRoomViewerModal";
import { useIssueTableSidebar } from "./useIssueTableSidebar";

type IssueTableAgGridRefactoredProps = {
  items: IIssueApi[];
  columnProps: IIssueTableAgGridColumnProps;
  loading: boolean;
  gridRef: React.RefObject<AgGridReact>;
};

const IssueTableAgGridRefactored: React.FC<IssueTableAgGridRefactoredProps> = ({ items, columnProps, gridRef }) => {
  const { issueBoardId, projectId } = columnProps;
  const dispatch = useAppDispatch();
  const store = useStore();
  const refContainer = useRef(null);
  const isSyncingSelectionRef = useRef(false);
  const columnsVisibility = useAppSelector(selectCurrentIssueColumnsVisibility);
  const currentColumnState = useAppSelector(selectCurrentIssueBoardColumnState, issueColumnLayoutEqual);
  const selectedIssueIdSet = useAppSelector(selectSelectedIssueIdSetOnCurrentIssueBoard);
  const sortedCol = useAppSelector((state) => state.issueReducerNew.sortedCol);
  const [updateIssue] = useDoUpdateIssueMutation();
  const [updateIssueSimple] = usePutProjectByProjectIdIssueUpdateSimpleMutation();
  const { closeRoomViewerModal, clickedRoomNumberInModal } = useIssueTableRoomViewerModal(issueBoardId);

  const { handleToolPanelVisibleChanged, icons, sideBar } = useIssueTableSidebar();
  const { groupedColumns } = useIssueTableColumns({
    columnProps,
    columnState: currentColumnState,
    columnsVisibility,
    sortedCol,
    updateIssue,
  });

  const defaultColDef = defaultIssueTableColDef;
  const { debouncedHandleToggleColumnVisibility, onColumnResized, onDragStarted, onDragStopped, onSortChanged } =
    useIssueTableColumnStateHandlers();
  const onCellEditRequest = useIssueTableCellEditing({
    issueBoardId,
    projectId,
    updateIssue,
    updateIssueSimple,
  });
  const gridStateKey = getIssueTableGridStateKey(issueBoardId);
  const { onGridReady: onGenericGridReady, persistState } = useAgGridReduxState<IIssueApi>(gridStateKey, undefined, {
    autoSizeOnFirstReady: false,
  });
  const getCurrentColumnState = useCallback(
    () =>
      getIssueColumnStateFromLocalStorage(issueBoardId) ??
      selectCurrentIssueBoardColumnState(store.getState() as IRootState),
    [issueBoardId, store],
  );
  const applyStoredColumnState = useCallback(
    (api: GridApi<IIssueApi>) => {
      const currentColumnState = getCurrentColumnState();
      const state =
        currentColumnState
          ?.filter((col) => col.colId != null)
          .sort((a, b) => (a.orderPriority ?? Number.MAX_SAFE_INTEGER) - (b.orderPriority ?? Number.MAX_SAFE_INTEGER))
          .map((col) => ({
            colId: col.colId!,
            width: col.width != null && col.width > 0 ? col.width : undefined,
          })) ?? [];

      if (state.length === 0) {
        return;
      }

      api.applyColumnState({ state, applyOrder: true });
    },
    [getCurrentColumnState],
  );
  const onFilterChanged = useCallback(() => {
    persistState();
  }, [persistState]);
  const syncGridSelectionFromRedux = useCallback(
    (api: GridApi<IIssueApi>) => {
      const nodesToSelect: IRowNode<IIssueApi>[] = [];
      const nodesToDeselect: IRowNode<IIssueApi>[] = [];

      api.forEachNode((node) => {
        const issueId = node.data?._id;
        if (issueId == null) {
          return;
        }

        const shouldBeSelected = selectedIssueIdSet.has(issueId);
        const isSelected = node.isSelected() === true;

        if (shouldBeSelected && !isSelected) {
          nodesToSelect.push(node);
        }

        if (!shouldBeSelected && isSelected) {
          nodesToDeselect.push(node);
        }
      });

      if (nodesToSelect.length === 0 && nodesToDeselect.length === 0) {
        return;
      }

      isSyncingSelectionRef.current = true;
      try {
        if (nodesToSelect.length > 0) {
          api.setNodesSelected({ nodes: nodesToSelect, newValue: true, source: "api" });
        }
        if (nodesToDeselect.length > 0) {
          api.setNodesSelected({ nodes: nodesToDeselect, newValue: false, source: "api" });
        }
      } finally {
        isSyncingSelectionRef.current = false;
      }
    },
    [selectedIssueIdSet],
  );
  const onGridReady = useCallback(
    (event: GridReadyEvent<IIssueApi>) => {
      onGenericGridReady(event);
      applyStoredColumnState(event.api);
      syncGridSelectionFromRedux(event.api);
    },
    [applyStoredColumnState, onGenericGridReady, syncGridSelectionFromRedux],
  );
  const onFirstDataRendered = useCallback(
    (event: FirstDataRenderedEvent<IIssueApi>) => {
      const hasAnyTask = items?.some((item) => item.connectedTasks?.length > 0) ?? false;
      const currentColumnState = getCurrentColumnState();
      const idColumnConfig = currentColumnState?.find((col) => col.colId === IssueFieldEnum.ID);

      if (idColumnConfig?.width == null) {
        event.api.setColumnWidths([{ key: IssueFieldEnum.ID, newWidth: hasAnyTask ? 140 : 100 }]);
      }
      syncGridSelectionFromRedux(event.api);
    },
    [getCurrentColumnState, items, syncGridSelectionFromRedux],
  );
  const onRowDataUpdated = useCallback(
    (event: RowDataUpdatedEvent<IIssueApi>) => {
      syncGridSelectionFromRedux(event.api);
    },
    [syncGridSelectionFromRedux],
  );
  const onSelectionChanged = useCallback(
    (event: SelectionChangedEvent<IIssueApi>) => {
      if (isSyncingSelectionRef.current || event.source === "api") {
        return;
      }

      dispatch(
        setSelectedIssueIdsInRedux({
          issueBoardId,
          issueIds: event.api
            .getSelectedRows()
            .map((issue) => issue._id)
            .filter(Boolean),
        }),
      );
    },
    [dispatch, issueBoardId],
  );

  useEffect(() => {
    const api = gridRef.current?.api;
    if (api == null) {
      return;
    }

    const sortedColumnState = api.getColumnState().find((columnState) => columnState.sort != null);
    if (sortedCol?.column != null && sortedCol.direction != null) {
      const sortDirection = sortedCol.direction.toLowerCase() as "asc" | "desc";
      if (sortedColumnState?.colId === sortedCol.column && sortedColumnState.sort === sortDirection) {
        return;
      }

      api.applyColumnState({
        defaultState: { sort: null },
        state: [{ colId: sortedCol.column, sort: sortDirection }],
      });
      return;
    }

    if (sortedColumnState == null) {
      return;
    }

    api.applyColumnState({ defaultState: { sort: null } });
  }, [gridRef, sortedCol]);

  const DetailCellRenderer = useCallback(
    (props: CustomCellRendererProps<IIssueApi>) =>
      props.data == null ? null : <IssueTableDetailCellRenderer data={props.data} />,
    [],
  );

  useEffect(() => {
    if (gridRef.current?.api == null) {
      return;
    }

    syncGridSelectionFromRedux(gridRef.current.api);
  }, [gridRef, selectedIssueIdSet, syncGridSelectionFromRedux]);

  return (
    <div className="d-flex flex-column w-100 h-100">
      <IssueTableRoomViewerModal opened={clickedRoomNumberInModal != null} onClose={closeRoomViewerModal} />

      <div className=" ag-theme-quartz h-100" id="myGrid" ref={refContainer}>
        <AgGridReact
          onDragStarted={onDragStarted}
          onDragStopped={onDragStopped}
          onColumnVisible={debouncedHandleToggleColumnVisibility}
          onColumnResized={onColumnResized}
          suppressHeaderFocus={true}
          cellSelection={true}
          rowHeight={ISSUE_TABLE_ROW_HEIGHT}
          headerHeight={ISSUE_TABLE_HEADER_HEIGHT}
          columnTypes={issueTableColumnTypes}
          maintainColumnOrder={true}
          ref={gridRef}
          getRowId={(p) => p.data._id}
          rowData={items}
          columnDefs={groupedColumns}
          stopEditingWhenCellsLoseFocus={true}
          readOnlyEdit={true}
          onCellEditRequest={onCellEditRequest}
          defaultColDef={defaultColDef}
          animateRows={false}
          icons={icons}
          onGridReady={onGridReady}
          onFirstDataRendered={onFirstDataRendered}
          onRowDataUpdated={onRowDataUpdated}
          onSelectionChanged={onSelectionChanged}
          onFilterChanged={onFilterChanged}
          sideBar={sideBar}
          rowSelection={{
            mode: "multiRow",
            checkboxes: true,
            checkboxLocation: "selectionColumn",
            headerCheckbox: true,
            selectAll: "currentPage",
            enableClickSelection: false,
          }}
          selectionColumnDef={{
            pinned: "left",
            lockPosition: true,
            suppressMovable: true,
            resizable: false,
            width: 44,
            minWidth: 44,
            maxWidth: 44,
          }}
          pagination={true}
          paginationPageSize={ISSUE_TABLE_PAGE_SIZE}
          paginationPageSizeSelector={ISSUE_TABLE_PAGE_SIZE_OPTIONS}
          suppressColumnVirtualisation={columnsVisibility.length < 25}
          suppressRowVirtualisation={items?.length < 100}
          rowBuffer={ISSUE_TABLE_ROW_BUFFER}
          masterDetail={true}
          detailRowAutoHeight={true}
          isRowMaster={(data) => data?.taskOrIssue === "ISSUE"}
          detailCellRenderer={DetailCellRenderer}
          localeText={AG_GRID_LOCALE_NOR}
          onToolPanelVisibleChanged={handleToolPanelVisibleChanged}
          onSortChanged={(e) => onSortChanged(e)}
          suppressCutToClipboard={true}
        />
      </div>
    </div>
  );
};

const issueTablePropsEqual = (
  prev: Readonly<IssueTableAgGridRefactoredProps>,
  next: Readonly<IssueTableAgGridRefactoredProps>,
) => prev.items === next.items && prev.columnProps === next.columnProps && prev.gridRef === next.gridRef;

const issueColumnLayoutEqual = (prev?: IStoredIssueColConfigs[], next?: IStoredIssueColConfigs[]): boolean => {
  if (prev === next) {
    return true;
  }

  const prevLength = prev?.length ?? 0;
  const nextLength = next?.length ?? 0;
  if (prevLength !== nextLength) {
    return false;
  }

  return (
    prev?.every(
      (prevColumn, index) =>
        prevColumn.colId === next?.[index]?.colId &&
        (prevColumn.orderPriority ?? Number.MAX_SAFE_INTEGER) ===
          (next?.[index]?.orderPriority ?? Number.MAX_SAFE_INTEGER),
    ) ?? nextLength === 0
  );
};

export default React.memo(IssueTableAgGridRefactored, issueTablePropsEqual);
